// src/student/pages/StudentMains.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  PenTool,
  Award,
  Send,
  CheckCircle,
  AlertCircle,
  Clock,
  FileText,
  RefreshCw,
  X,
  FileCheck,
} from "lucide-react";
import { studentPortalAPI } from "../services/studentApi";
import { useStudentAuth } from "../context/StudentAuthContext";

export default function StudentMains() {
  const { studentUser } = useStudentAuth();
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Answer Submission Modal State
  const [activeInitiative, setActiveInitiative] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [answerFile, setAnswerFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [submitError, setSubmitError] = useState("");

  const fetchInitiatives = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await studentPortalAPI.getPublishedInitiatives();
      if (res.success && Array.isArray(res.data)) {
        setInitiatives(res.data);
      }
    } catch (err) {
      setError(err.message || "Failed to load Mains initiatives.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitiatives();
  }, [fetchInitiatives]);

  // Real-time word counter
  const wordCount = useMemo(() => {
    if (!answerText) return 0;
    const words = answerText.trim().split(/\s+/).filter(Boolean);
    return words.length;
  }, [answerText]);

  const handleOpenSubmission = (init) => {
    setActiveInitiative(init);
    setAnswerText("");
    setAnswerFile(null);
    setSubmitSuccess("");
    setSubmitError("");
  };

  const handleCloseSubmission = () => {
    setActiveInitiative(null);
    setAnswerText("");
    setAnswerFile(null);
    setSubmitSuccess("");
    setSubmitError("");
  };

  const handleSubmitAnswer = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    if (!answerText.trim() && !answerFile) {
      setSubmitError("Please write your answer or upload a handwritten PDF.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("studentEmail", studentUser?.email || "");
      formData.append("studentName", studentUser?.name || "Aspirant");
      formData.append("answerText", answerText);
      formData.append("wordCount", wordCount);
      if (answerFile) {
        formData.append("answerPdf", answerFile);
        formData.append("answerFile", answerFile);
      }

      const res = await studentPortalAPI.submitMainsAnswer(activeInitiative._id, formData);
      if (res.success) {
        setSubmitSuccess("Answer submitted successfully! Our evaluators will review it.");
        setTimeout(() => {
          handleCloseSubmission();
          fetchInitiatives();
        }, 1500);
      }
    } catch (err) {
      setSubmitError(err.message || "Failed to submit Mains answer. Please check deadline.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-red-950 via-slate-900 to-[#0b1528] rounded-2xl p-6 text-white shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300 bg-white/10 px-2.5 py-0.5 rounded-full">
              Free UPSC Initiative
            </span>
            <span className="text-xs text-slate-300 font-medium">Daily Answer Writing (DAW)</span>
          </div>
          <h1 className="text-xl md:text-2xl font-black tracking-tight">
            Mains Answer Writing Initiatives
          </h1>
          <p className="text-xs text-slate-300 mt-1 max-w-xl">
            Sharpen your UPSC CSE Mains answer structure, arguments, and content with curated GS questions, word limits, and mentor evaluations.
          </p>
        </div>

        <button
          onClick={fetchInitiatives}
          className="self-start md:self-auto flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold backdrop-blur-xs transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Initiatives Grid */}
      {loading ? (
        <div className="py-16 text-center text-slate-400 text-sm">
          Loading Mains initiatives...
        </div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      ) : initiatives.length === 0 ? (
        <div className="py-16 bg-white rounded-2xl border border-slate-200 text-center">
          <PenTool className="mx-auto text-slate-300 mb-2" size={36} />
          <p className="text-sm font-semibold text-slate-700">No active Mains initiatives</p>
          <p className="text-xs text-slate-400 mt-1">New questions will be published daily as per schedule.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {initiatives.map((init) => {
            const isDeadlinePassed = init.submissionDeadline
              ? new Date(init.submissionDeadline) < new Date()
              : false;

            return (
              <div
                key={init._id}
                className="bg-white rounded-2xl border border-slate-200 hover:border-red-800/40 hover:shadow-lg transition-all duration-200 flex flex-col justify-between p-6"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-red-900 bg-red-50 border border-red-100 px-2.5 py-0.5 rounded-full">
                      {init.gsPaper || "GS General Studies"}
                    </span>
                    <span className="text-xs font-semibold text-slate-500 flex items-center gap-1">
                      <Award size={14} className="text-amber-600" />
                      {init.maxMarks || 15} Marks
                    </span>
                  </div>

                  <h3 className="text-base font-bold text-slate-900 mb-2">
                    {init.title}
                  </h3>

                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-800 leading-relaxed font-serif mb-4">
                    {init.questionText || init.description}
                  </div>

                  {init.instructions && (
                    <p className="text-[11px] text-slate-500 italic mb-4">
                      💡 Note: {init.instructions}
                    </p>
                  )}

                  {/* Specs Grid */}
                  <div className="flex items-center gap-4 text-xs text-slate-500 font-medium py-2 border-y border-slate-100">
                    <span className="flex items-center gap-1">
                      <FileText size={13} />
                      Limit: <strong className="text-slate-800">{init.wordLimit || 250} Words</strong>
                    </span>
                    {init.submissionDeadline && (
                      <span className="flex items-center gap-1">
                        <Clock size={13} />
                        Deadline: {new Date(init.submissionDeadline).toLocaleDateString("en-IN")}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-5 pt-2 flex items-center justify-between">
                  {isDeadlinePassed ? (
                    <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg">
                      Deadline Closed
                    </span>
                  ) : (
                    <span className="text-xs text-emerald-700 font-semibold bg-emerald-50 px-2.5 py-1 rounded-lg">
                      Open for Submissions
                    </span>
                  )}

                  <button
                    disabled={isDeadlinePassed}
                    onClick={() => handleOpenSubmission(init)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-900 hover:bg-red-800 text-white rounded-xl text-xs font-bold transition-all shadow-xs disabled:opacity-40"
                  >
                    <PenTool size={13} />
                    <span>Write Answer</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Answer Writing Modal */}
      {activeInitiative && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-2xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 shadow-2xl border border-slate-200 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-red-900 bg-red-50 px-2.5 py-0.5 rounded-full border border-red-100">
                  {activeInitiative.gsPaper}
                </span>
                <span className="text-xs text-slate-500 font-medium">
                  {activeInitiative.maxMarks || 15} Marks
                </span>
              </div>
              <button
                onClick={handleCloseSubmission}
                className="text-slate-400 hover:text-slate-700 text-sm font-bold p-1 rounded"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {/* Question Banner */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs font-serif text-slate-900 leading-relaxed">
                <strong>Question:</strong> {activeInitiative.questionText || activeInitiative.description}
              </div>

              {submitError && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                  <span>{submitError}</span>
                </div>
              )}

              {submitSuccess && (
                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-800 flex items-start gap-2">
                  <CheckCircle size={15} className="flex-shrink-0 mt-0.5" />
                  <span>{submitSuccess}</span>
                </div>
              )}

              {/* Textarea Answer */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold text-slate-800">
                    Type Your Answer
                  </label>
                  <span
                    className={`text-xs font-bold ${
                      wordCount > (activeInitiative.wordLimit || 250)
                        ? "text-rose-600"
                        : "text-slate-500"
                    }`}
                  >
                    {wordCount} / {activeInitiative.wordLimit || 250} Words
                  </span>
                </div>

                <textarea
                  rows={8}
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  placeholder="Structure your answer into: Introduction (context/definition) -> Body paragraphs (arguments, constitutional provisions, committees, diagrams) -> Forward-looking conclusion..."
                  className="w-full p-4 text-xs font-sans text-slate-800 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-900 focus:border-red-900 outline-none leading-relaxed"
                />
              </div>

              {/* Handwritten PDF Upload */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200">
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Upload Handwritten Answer Sheet (Optional PDF)
                </label>
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => setAnswerFile(e.target.files[0] || null)}
                  className="text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-slate-200 file:text-slate-800 hover:file:bg-slate-300 cursor-pointer"
                />
                {answerFile && (
                  <p className="text-[11px] text-emerald-700 mt-1 font-medium flex items-center gap-1">
                    <FileCheck size={13} /> {answerFile.name} ready for upload
                  </p>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
              <button
                type="button"
                onClick={handleCloseSubmission}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={submitting}
                onClick={handleSubmitAnswer}
                className="flex items-center gap-1.5 px-5 py-2 bg-red-900 hover:bg-red-800 text-white rounded-xl text-xs font-bold shadow-xs hover:shadow-md transition-all disabled:opacity-50"
              >
                <Send size={13} />
                <span>{submitting ? "Uploading Answer..." : "Submit Answer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
